import type { Ref } from 'react'
import { useTranslation } from 'react-i18next'
import type { CommandListResponse } from '@/entities/command'
import { cx } from '@/shared/lib'
import { CounterBadge } from '@/shared/ui/CounterBadge'
import { Icon } from '@/shared/ui/Icon'
import { isRunning } from '../lib/commandView'
import s from './ProcessMonitorButton.module.css'

type ProcessMonitorButtonProps = {
  data: CommandListResponse | undefined
  isLoading: boolean
  isError: boolean
  onOpen: () => void
  ref?: Ref<HTMLButtonElement>
}

const TONE_BADGE = { active: 'accent', danger: 'danger', muted: 'neutral' } as const

const BARS = [0, 1, 2, 3, 4]

/**
 * Кнопка монитора в хвосте меню: волна-индикатор, подпись «Активные процессы» и число,
 * остальное — в модалке. Состояния по данным:
 * - есть активные (идут в 1С или ждут очереди) — волна танцует всегда, по капсуле пробегает
 *   блик, число — идущие и ждущие;
 * - работы нет, но есть сбои — красный тон и число сбоев;
 * - тишина — приглушённый тон и ноль; очередь недоступна — знак предупреждения без числа;
 * - загрузка — приглушённая волна без числа.
 * Ход первой команды уходит в доступное имя. Кнопка открывает модалку в любом состоянии:
 * там и сбой, и повтор.
 */
export function ProcessMonitorButton({
  data,
  isLoading,
  isError,
  onOpen,
  ref,
}: ProcessMonitorButtonProps) {
  const { t } = useTranslation('processes')

  const summary = data?.summary
  const running = summary?.running ?? 0
  const queued = summary?.queued ?? 0
  const failed = summary?.failed ?? 0
  const active = running + queued
  const first = data?.items.find(isRunning)
  const ready = !isLoading && !isError

  const tone = !ready ? 'muted' : active > 0 ? 'active' : failed > 0 ? 'danger' : 'muted'
  const count = !ready ? null : active > 0 ? active : failed
  /* Есть активные — волна танцует всегда, идут они уже в 1С или ждут своей очереди */
  const moving = ready && active > 0

  const ticker = isLoading
    ? t('button.loading')
    : isError
      ? t('button.unavailable')
      : first !== undefined
        ? first.progress !== null
          ? t('button.ticker', {
              type: t(`typeShort.${first.type}`),
              number: first.subject.number,
              progress: first.progress,
            })
          : t('button.tickerNoProgress', { type: t(`typeShort.${first.type}`) })
        : queued > 0
          ? t('button.tickerQueued', { count: queued })
          : failed > 0
            ? t('button.tickerFailed', { count: failed })
            : t('button.idle')

  return (
    <button
      ref={ref}
      type="button"
      className={cx(s.root, s[tone], moving && s.live)}
      data-tone={tone}
      onClick={onOpen}
      aria-label={t('button.open', {
        title: count !== null ? t('button.labelCount', { count }) : t('button.label'),
        ticker,
      })}
      aria-busy={isLoading || undefined}
    >
      {isError ? (
        <Icon name="warn" size="sm" />
      ) : (
        /*
         * data-motion="essential": волна — индикатор выполнения, как спиннер, и глобальное
         * «поменьше движения» (`base.css`) её не гасит; для этой настройки у неё свой, спокойный темп
         */
        <span className={cx(s.wave, moving && s.dance)} aria-hidden="true">
          {BARS.map((bar) => (
            <span key={bar} className={s.bar} data-motion="essential" />
          ))}
        </span>
      )}

      <span className={s.label}>{t('button.label')}</span>

      {count !== null ? (
        /* Ключ — число: смена количества заново проигрывает появление */
        <CounterBadge
          key={count}
          className={s.count}
          value={count}
          showZero
          size="xs"
          tone={TONE_BADGE[tone]}
          decorative
        />
      ) : null}
    </button>
  )
}
